import React, { useState } from 'react';
import { Container, Form, Button, Alert, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { createCommunity, joinCommunityAsAdmin } from '../../services/communityService';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faArrowLeft, 
  faSave, 
  faTimes, 
  faUsers
} from "@fortawesome/free-solid-svg-icons";

function CreateCommunity() {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const navigate = useNavigate();
  const { refreshCsrfToken, userCommunities } = useAuth();
  const { t } = useLanguage();
  const { isMobile } = useNavigation();

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('communities.errors.nameRequired');
    } else if (formData.name.length < 3) {
      newErrors.name = t('communities.errors.nameMinLength');
    } else if (formData.name.length > 100) {
      newErrors.name = t('communities.errors.nameMaxLength');
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = t('communities.errors.descriptionMaxLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      // Refresh CSRF token before making the request
      await refreshCsrfToken();
      
      // Create the community
      const response = await createCommunity(formData);

      if (response && response.community) {
        setSuccessMessage(t('communities.communityCreatedSuccess', { name: response.community.name }));

        // The backend should have already added the user as admin,
        // but as a fallback, try to join as admin explicitly
        try {
          await joinCommunityAsAdmin(response.community.id, userCommunities.length === 0);

          // Show success for 2 seconds, then redirect
          setTimeout(() => {
            navigate(`/manage-community/${response.community.id}`);
          }, 2000);
        } catch (joinError) {

          // Check if it's a specific error about already being a member
          if (joinError.response?.data?.message?.includes('already a member')) {
            // This is fine, the backend already added the user
            setTimeout(() => {
              navigate(`/manage-community/${response.community.id}`);
            }, 2000);
          } else {
            setErrorMessage(t('communities.errors.createdButJoinError'));
          }
        }
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.error ||
        t('communities.errors.createError')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // MOBILE VIEW
  if (isMobile) {
    return (
      <div className="mobile-song-library">
        <div className="mobile-song-header">
          <div className="mobile-song-header-content">
            <Button
              variant="link"
              className="p-0 text-white"
              onClick={() => navigate(-1)}
              style={{ marginRight: '10px' }}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Button>
            <h1>{t('communities.newCommunity')}</h1>
          </div>
        </div>
        
        <div className="mobile-song-content">
          {successMessage && (
            <Alert variant="success" className="mb-3">{successMessage}</Alert>
          )}
          
          {errorMessage && (
            <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
          )}
          
          <div className="card bg-dark border-0 mb-3">
            <div className="card-body">
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="communityName">
                  <Form.Label>{t('communities.communityName')} *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    isInvalid={!!errors.name}
                    className="bg-dark text-white border-secondary"
                    placeholder={t('communities.communityName')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    {t('communities.shortNameInstruction')}
                  </Form.Text>
                </Form.Group>
                
                <Form.Group className="mb-4" controlId="communityDescription">
                  <Form.Label>{t('communities.communityDescription')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    isInvalid={!!errors.description}
                    className="bg-dark text-white border-secondary"
                    placeholder={t('communities.communityDescription')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.description}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    {t('communities.descriptionInstruction')}
                  </Form.Text>
                </Form.Group>
                
                <div className="d-grid gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('communities.creating')}
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} className="me-2" />
                        {t('communities.create')}
                      </>
                    )}
                  </Button>

                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate(-1)}
                    disabled={isSubmitting}
                  >
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    {t('communities.cancel')}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // DESKTOP VIEW
  return (
    <Container className="py-5">
      <Card className="shadow border-0 rounded-4">
        <Card.Body className="p-4">
          <h2 className="text-center mb-4">{t('communities.createNew')}</h2>

          {successMessage && (
            <Alert variant="success">{successMessage}</Alert>
          )}

          {errorMessage && (
            <Alert variant="danger">{errorMessage}</Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="communityName">
              <Form.Label>{t('communities.communityName')} *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                isInvalid={!!errors.name}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                {t('communities.shortNameInstruction')}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4" controlId="communityDescription">
              <Form.Label>{t('communities.communityDescription')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="description"
                value={formData.description}
                onChange={handleChange}
                isInvalid={!!errors.description}
              />
              <Form.Control.Feedback type="invalid">
                {errors.description}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                {t('communities.descriptionInstruction')}
              </Form.Text>
            </Form.Group>

            <div className="d-grid gap-2">
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('communities.creating') : t('communities.create')}
              </Button>

              <Button
                variant="outline-secondary"
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
              >
                {t('communities.cancel')}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default CreateCommunity;